import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Select, Empty } from 'antd';
import ChartCard from './ChartCard';
import { apiClient, API_ENDPOINTS } from '../../utils/api';
import styled, { keyframes } from 'styled-components';

const COLORS = {
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  pending: '#FBBF24',
  processing: '#22D3EE',
  completed: '#10B981',
  failed: '#F87171',
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 350px;
  animation: ${fadeIn} 0.6s ease-out;
`;

const SelectContainer = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-end;
`;

const StyledSelect = styled(Select)`
  width: 150px;
  
  .ant-select-selector {
    background: #161B22 !important;
    border: 1px solid #1f2a37 !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-select-selection-item {
    color: ${COLORS.textPrimary} !important;
  }
`;

interface SweepTrendData {
  date: string;
  PENDING: number;
  PROCESSING: number;
  COMPLETED: number;
  FAILED: number;
}

interface SweepTrendsChartProps {
  isAdmin?: boolean;
  merchantCompanyId?: string;
}

const SweepTrendsChart: React.FC<SweepTrendsChartProps> = ({ isAdmin = true }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<SweepTrendData[]>([]);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const endpoint = isAdmin
        ? `${API_ENDPOINTS.ADMIN_GET_SWEEP_TRENDS}?days=${days}`
        : `${API_ENDPOINTS.MERCHANT_GET_SWEEP_TRENDS}?days=${days}`;
      const response = await apiClient.get(endpoint);
      if (response.data?.status === 'success' && response.data?.data) {
        setData(response.data.data);
        console.log('Sweep Trends Data:', response.data.data);
      } else {
        console.warn('Sweep Trends: Unexpected response format', response.data);
      }
    } catch (error: any) {
      console.error('Error fetching sweep trends:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [days, isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: '#161B22',
          border: '1px solid #1f2a37',
          borderRadius: '8px',
          padding: '12px',
          color: COLORS.textPrimary,
        }}>
          <p style={{ margin: 0, marginBottom: '8px', fontWeight: 600 }}>
            {payload[0].payload.date ? formatDate(payload[0].payload.date) : ''}
          </p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ margin: 0, color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <ChartCard
      title="Sweep Trends Over Time"
      extra={
        <SelectContainer>
          <StyledSelect
            value={days}
            onChange={(value) => setDays(value as number)}
            style={{ width: 150 }}
          >
            <Select.Option value={7}>Last 7 days</Select.Option>
            <Select.Option value={30}>Last 30 days</Select.Option>
            <Select.Option value={90}>Last 90 days</Select.Option>
          </StyledSelect>
        </SelectContainer>
      }
      loading={loading}
    >
      {!loading && data.length === 0 ? (
        <Empty 
          description={
            <div>
              <p style={{ marginBottom: '8px', color: COLORS.textPrimary }}>
                No sweep trend data available
              </p>
              <p style={{ fontSize: '12px', color: COLORS.textSecondary }}>
                Data will appear here once sweep transactions are created
              </p>
            </div>
          }
        />
      ) : (
        <ChartContainer>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
              <defs>
                <linearGradient id="pendingGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.pending} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={COLORS.pending} stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="processingGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.processing} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={COLORS.processing} stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="completedGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.completed} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={COLORS.completed} stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="failedGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.failed} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={COLORS.failed} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2a37" opacity={0.5} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                stroke={COLORS.textSecondary}
                style={{ fontSize: '12px' }}
                tick={{ fill: COLORS.textSecondary }}
              />
              <YAxis stroke={COLORS.textSecondary} style={{ fontSize: '12px' }} tick={{ fill: COLORS.textSecondary }} />
              <Tooltip 
                content={<CustomTooltip />}
                cursor={{ stroke: COLORS.accent, strokeWidth: 1, strokeDasharray: '5 5' }}
              />
              <Legend
                wrapperStyle={{ color: COLORS.textPrimary, paddingTop: '20px' }}
                iconType="line"
                iconSize={16}
              />
              <Line
                type="monotone"
                dataKey="PENDING"
                stroke={COLORS.pending}
                strokeWidth={3}
                dot={{ r: 4, fill: COLORS.pending, strokeWidth: 2, stroke: '#161B22' }}
                activeDot={{ r: 6, fill: COLORS.pending }}
                name="Pending"
                animationDuration={1000}
                animationEasing="ease-out"
              />
              <Line
                type="monotone"
                dataKey="PROCESSING"
                stroke={COLORS.processing}
                strokeWidth={3}
                dot={{ r: 4, fill: COLORS.processing, strokeWidth: 2, stroke: '#161B22' }}
                activeDot={{ r: 6, fill: COLORS.processing }}
                name="Processing"
                animationDuration={1000}
                animationEasing="ease-out"
              />
              <Line
                type="monotone"
                dataKey="COMPLETED"
                stroke={COLORS.completed}
                strokeWidth={3}
                dot={{ r: 4, fill: COLORS.completed, strokeWidth: 2, stroke: '#161B22' }}
                activeDot={{ r: 6, fill: COLORS.completed }}
                name="Completed"
                animationDuration={1000}
                animationEasing="ease-out"
              />
              <Line
                type="monotone"
                dataKey="FAILED"
                stroke={COLORS.failed}
                strokeWidth={3}
                dot={{ r: 4, fill: COLORS.failed, strokeWidth: 2, stroke: '#161B22' }}
                activeDot={{ r: 6, fill: COLORS.failed }}
                name="Failed"
                animationDuration={1000}
                animationEasing="ease-out"
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      )}
    </ChartCard>
  );
};

export default SweepTrendsChart;

